import React from "react";
import {
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineMusicalNote,
} from "react-icons/hi2";
import { useTranslation } from "react-i18next";

interface EventHeaderProps {
  title: string;
  description: string;
  date: string;
  time: string;
  type?: string;
}

function formatEventDate(dateStr: string, locale: string) {
  if (!dateStr) return "";
  // Prefer YYYY-MM-DD (avoids timezone shift from Date parsing)
  const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let d: Date;
  if (iso) {
    d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  } else {
    d = new Date(dateStr);
  }
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function relativeLabel(
  dateStr: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (!dateStr) return null;
  const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);

  if (diffDays === 0) return t("today");
  if (diffDays === 1) return t("tomorrow");
  if (diffDays === -1) return t("yesterday");
  if (diffDays > 1 && diffDays <= 7) return t("inDays", { count: diffDays });
  if (diffDays < -1 && diffDays >= -7)
    return t("daysAgo", { count: Math.abs(diffDays) });
  return null;
}

const EventHeader: React.FC<EventHeaderProps> = ({
  title,
  description,
  date,
  time,
  type,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es" : "en";
  const formatted = formatEventDate(date, locale);
  const relative = relativeLabel(date, t);

  return (
    <div className="flex items-start gap-4 sm:gap-5 min-w-0 w-full">
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
        {type === "Worship" ? (
          <HiOutlineMusicalNote className="w-7 h-7" strokeWidth={2} />
        ) : (
          <HiOutlineCalendarDays className="w-7 h-7" strokeWidth={2} />
        )}
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          {type && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              {type === "Worship" ? t("worship") || "Worship" : type}
            </span>
          )}
          {relative && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {relative}
            </span>
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 tracking-tight break-words [overflow-wrap:anywhere]">
          {title}
        </h1>
        {description && (
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm sm:text-base leading-relaxed max-w-2xl">
            {description}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-gray-600 dark:text-gray-400">
          {formatted && (
            <span className="inline-flex items-center gap-1.5">
              <HiOutlineCalendarDays className="w-4 h-4 text-gray-400 shrink-0" />
              {formatted}
            </span>
          )}
          {time && (
            <span className="inline-flex items-center gap-1.5">
              <HiOutlineClock className="w-4 h-4 text-gray-400 shrink-0" />
              {time}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventHeader;
